// Configuration for formatting AI text with page links
export interface PageLinkOptions {
  pathname: string;
  searchParams: URLSearchParams;
}

// Type for parsed text segments
type TextSegment = {
  type: "text" | "match";
  text: string;
};

/**
 * Regular expression to match page references in text.
 *
 * Matches:
 * - Single pages: "page 1", "Page 228"
 * - Multiple pages: "pages 1, 2 and 3", "pages 1-3", "pages 1 & 2"
 * - Pages with .0 suffix: "page 1.0" (sometimes added by AI)
 *
 * Avoids:
 * - Section references: "page 8.2.7" (has additional decimals)
 * - Nested decimals: "page 1.2.3.4"
 */
const PAGE_REFERENCE_PATTERN = /pages?\s\d+(\.0)?(?!(\.\d+)+)(\s?[,&\-–—]?\s?(and)?\s?\d+(\.0)?(?!(\.\d+)+))*/gi;

/**
 * Regular expression to extract individual page numbers from a page reference.
 * Matches: "page 1", "pages 2", "3", "4.0"
 */
const PAGE_NUMBER_PATTERN = /(pages?\s)?\d+(\.0)?/gi;

/**
 * Splits a string by regex matches, preserving both matched and non-matched segments.
 * This allows us to process only the parts that match while keeping the rest intact.
 */
const parseTextSegments = (text: string, pattern: RegExp): TextSegment[] => {
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset regex state for global patterns
  pattern.lastIndex = 0;

  match = pattern.exec(text);
  while (match !== null) {
    // Add text before the match
    if (lastIndex < match.index) {
      segments.push({
        type: "text",
        text: text.slice(lastIndex, match.index),
      });
    }

    // Add the matched text
    if (match[0].trim()) {
      segments.push({
        type: "match",
        text: match[0],
      });
    }

    lastIndex = match.index + match[0].length;
    match = pattern.exec(text);
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    segments.push({
      type: "text",
      text: text.slice(lastIndex),
    });
  }

  return segments;
};

/**
 * Creates a URL with the page query parameter.
 */
const buildPageUrl = (pageNumber: string, pathname: string, searchParams: URLSearchParams): string => {
  const params = new URLSearchParams(searchParams);
  params.set("page", pageNumber);
  return `${pathname}?${params.toString()}`;
};

/**
 * Extracts the page number from text like "page 123" or "123.0".
 */
const extractPageNumber = (text: string): string => {
  // Remove ".0" suffix if present
  const normalized = text.replace(/\.0$/, "");
  // Extract just the digits
  return normalized.replace(/\D/g, "");
};

/**
 * Converts page references in text to markdown links.
 * Example: "page 1, 2 and 3" -> "[page 1](url), [2](url) and [3](url)"
 */
const convertPageReferencesToLinks = (text: string, linkOptions?: PageLinkOptions): string => {
  const segments = parseTextSegments(text, PAGE_NUMBER_PATTERN);

  return segments
    .map((segment) => {
      if (segment.type === "text") {
        return segment.text;
      }

      // Clean up the page reference text (remove .0 suffix)
      const displayText = segment.text.replace(/\.0$/, "");
      const pageNumber = extractPageNumber(segment.text);

      if (!pageNumber) {
        return segment.text;
      }

      // Generate the link URL if options are provided
      const url = linkOptions
        ? buildPageUrl(pageNumber, linkOptions.pathname, linkOptions.searchParams)
        : `#page-${pageNumber}`;

      return `[${displayText}](${url})`;
    })
    .join("");
};

/**
 * Processes AI-generated text to convert page references into clickable markdown links
 * and style citations in double brackets.
 * Preserves line breaks and only processes matching page references.
 */
export const formatPageReferences = (text: string | null | undefined, linkOptions?: PageLinkOptions): string => {
  if (!text) return "";

  // Process each line independently to preserve formatting
  const lines = text.split("\n");

  return lines
    .map((line) => {
      const segments = parseTextSegments(line, PAGE_REFERENCE_PATTERN);

      return segments
        .map((segment) => {
          if (segment.type === "match") {
            // Convert the matched page reference to links
            return convertPageReferencesToLinks(segment.text, linkOptions);
          }
          return segment.text;
        })
        .join("");
    })
    .join("\n");
};
